import { ApolloError, useLazyQuery, useReactiveVar } from '@apollo/client'
import DateFnsUtils from '@date-io/date-fns'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import Countries from '@deltek/specification-client/core/Constants/countries'
import States from '@deltek/specification-client/core/Constants/usStates'
import FormControlOutlined from '@deltek/specification-client/core/Forms/Settings/FormControlOutlined'
import { Address } from '@deltek/specification-client/core/Models/address'
import {
    FormProps,
    FormPropsObject,
} from '@deltek/specification-client/core/Models/formProps'
import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import {
    StateSetStateProps,
    hasError,
    isFormValueDirty,
    updateFormState,
    validateFormState,
} from '@deltek/specification-client/core/Utilities/formUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import TextField, { TextFieldProps } from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { Theme, useTheme } from '@material-ui/core/styles'
import makeStyles from '@material-ui/core/styles/makeStyles'
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { GetProjectBulkAddStatus } from 'api/graphQL/queries/projectBulk.queries'
import { ownerId, updateOwnerId } from 'api/graphQL/reactiveVars/projectHome'
import { BackendProcessStatus } from 'api/graphQL/types/backendProcess.types'

import { CreateProjectResult, Project } from 'Models/project'
import {
    activeHeaderFooter,
    setFooterMergeFields,
    setHeaderMergeFields,
} from 'api/graphQL/reactiveVars/headerFooterTemplate'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import ProgressDialog from 'components/ToMoveToSCS/ProgressDialog'
import { ExecutionResult } from 'graphql'
import { MilestoneInput } from 'models/milestoneInput'
import { ParameterInput } from 'models/parameterInput'
import { ToggleMergeFieldPreviewInput } from 'models/projectHeaderAndFooter'
import { ProjectInput } from 'models/projectInput'
import React, { useCallback } from 'react'
import {
    formatDateWithTimezone,
    formatLocalDate,
    isDateRangeValid,
    validateDate,
} from 'shared/utils/dateUtils'
import { isParameterMultiSelect } from 'shared/utils/projectParameterUtils'
import { useToggleMergeFieldPreview } from '../FormatPage/components/HeaderFooterPreviewPanel/useToggleMergeFieldPreview'
import ProjectInputMulti from './ProjectInputMulti'
import ProjectInputSingle from './ProjectInputSingle'
import useRemoveOwnerDialog from './useRemoveOwnerDialog'

const useStyles = makeStyles((theme: Theme) => ({
    row: {
        maxWidth: '100%',
    },
    wrap: {
        margin: 'auto',
        flexDirection: 'column',
        display: 'flex',
        height: '100%',
        width: '100%',
    },
    header: {
        margin: 'unset',
        textAlign: 'left',
        width: '100%',
        height: pxToRem(47),
        color: '#fff',
        fontSize: pxToRem(23),
        background: 'linear-gradient(145.96deg, #C68226 20.1%, #BD7257 79.78%)',
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textTransform: 'uppercase',
    },
    container: {
        flex: 1,
        overflowY: 'auto',
    },
    footer: {
        height: 80,
        border: '1px solid #C9C9C9',
    },
    paddingLeft: {
        '&.MuiGrid-item': {
            paddingLeft: theme.spacing(2.25),
        },
        '&.MuiGrid-container': {
            paddingLeft: theme.spacing(2.25),
        },
    },
    marginUnset: {
        margin: 'unset',
    },

    firstColumnPadding: {
        '&.MuiGrid-item': {
            paddingLeft: 0,
        },
        '& .roundButtonContainer': {
            float: 'right',
        },
    },
    newProjectFormInputTop: {
        marginTop: theme.spacing(0.875),
    },

    dividerContainer: {
        '&.MuiGrid-item': {
            paddingTop: pxToRem(0),
            paddingBottom: pxToRem(6),
        },
    },
    dividerHeading: {
        color: '#211f20',
        fontSize: pxToRem(14),
        textTransform: 'uppercase',
        '&.MuiGrid-item': {
            paddingBottom: theme.spacing(0.75),
        },
    },
    divider: {
        width: '100%',
        border: '2px solid #F2F2F2',
    },

    formControl: {
        height: 'unset',
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
    buttonText: {
        fontSize: pxToRem(14),
    },
    selectLabel: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    text: {
        fontSize: pxToRem(14),
    },
    root: {
        '& legend span': {
            fontSize: '1rem',
        },
    },
    paddingRight: {
        paddingRight: theme.spacing(0),
    },
    dateAdornment: {
        '& button': {
            marginRight: theme.spacing(-2.25),
        },
    },
    saveButtonCell: {
        '& button': {
            width: theme.spacing(10.4),
        },
    },
    cancelButtonCell: {
        '& button': {
            width: theme.spacing(10.4),
            '&:hover': {
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.text.secondary}`,
                backgroundColor: 'rgba(167, 137, 102, 0.25)',
            },
        },
    },
    helpertextContained: {
        marginLeft: theme.spacing(1),
        marginRight: 0,
    },
}))

const dateFormat = 'yyyy-MM-dd'

export declare type multiSelectNameValuePropType =
    | nameValuePropType
    | 'multiple'
export interface NewProjectFormProps extends FormPropsObject {
    [key: string]: FormProps
}

export interface NewProjectProps {
    firmProjectGroupLoading: boolean
    projectPhases: Record<nameValuePropType, string>[]
    projectGroups: Record<nameValuePropType, string>[]
    firmProjectGroupError: ApolloError | null
    constructionBudgets?: Record<nameValuePropType, string>[]
    showGlobalSnackbar: (options?: any) => Promise<ExecutionResult<any>>
    toggleFormDirty: (value: boolean) => void
    onCancel?: () => void
    associationModifierLists: Record<
        multiSelectNameValuePropType,
        string | boolean
    >[]
    getModifiers: (
        AssociationModifierListsId: string
    ) => Record<nameValuePropType, string>[]
    defaultFormState: NewProjectFormProps
    firmId: string
    isEditMode: boolean
    createProjectLoading: boolean
    createProjectError: ApolloError | null
    createProjectData: { createProject: CreateProjectResult | null }
    createProject: (
        options?: any
    ) => Promise<ExecutionResult<{ createProject: CreateProjectResult | null }>>
    updateProjectLoading: boolean
    updateProjectError: ApolloError | null
    updateProjectData: { updateProject: Partial<Project> | null }
    updateProject: (
        options?: any
    ) => Promise<ExecutionResult<{ updateProject: Partial<Project> | null }>>
    triggerProjectListReload: VoidFunction
    setSkipCount?: (val: number) => void
    openProjectGroupDialog: VoidFunction
    defaultProjectGroup?: Record<nameValuePropType, string> | null | undefined
    isAdmin?: boolean
    openProjectOwnerDialog?: VoidFunction
    updateParameterOptionsList?: (ownerId: string, ownerListId: string) => void
    defaultProjectOwner?: string | null | undefined
    isOwnerDisabled?: boolean
    projectDate?: string | null | undefined
    issueDate?: string | null | undefined
    addendumNumber?: string | null | undefined
    issueFor?: string | null | undefined
    levelOfSpecifications: Record<nameValuePropType, string>[]
    isLevelOfSpecificationConfirmationDialogOpen: boolean
    isLevelOfSpecificationChangeConfirmed: boolean
    setIsLevelOfSpecificationConfirmationDialogOpen?: (val: boolean) => void
    setIsLevelOfSpecificationChangeConfirmed?: (val: boolean) => void
}

export const CalendarTextField: React.FC<TextFieldProps> = (props) => {
    const classes = useStyles()
    return (
        <TextField
            fullWidth
            {...props}
            className={clsx(props.className, classes.paddingRight)}
            InputLabelProps={{
                ...props.InputLabelProps,
                className: clsx(
                    props.InputLabelProps?.className,
                    classes.text,
                    classes.selectLabel
                ),
            }}
            InputProps={{
                ...props.InputProps,
                classes: {
                    input: classes.text,
                    root: classes.root,
                },
            }}
            inputProps={{
                ...props.inputProps,
                'aria-autocomplete': 'none',
                autoComplete: 'chrome-off',
            }}
            FormHelperTextProps={{
                ...props.FormHelperTextProps,
                classes: {
                    contained: classes.helpertextContained,
                },
            }}
        />
    )
}

export const sortOptionsAlphabetically = (
    unsortedOptionList: Record<nameValuePropType, string>[]
) => {
    return unsortedOptionList.sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0
    )
}

const NewProject: React.FC<NewProjectProps> = ({
    firmProjectGroupLoading,
    projectPhases,
    projectGroups,
    firmProjectGroupError,
    constructionBudgets,
    showGlobalSnackbar,
    toggleFormDirty,
    onCancel,
    associationModifierLists,
    getModifiers,
    defaultFormState,
    isEditMode = false,
    firmId,
    createProjectLoading,
    createProjectError,
    createProjectData,
    createProject,
    updateProjectLoading,
    updateProjectError,
    updateProjectData,
    updateProject,
    triggerProjectListReload,
    setSkipCount,
    openProjectGroupDialog,
    defaultProjectGroup,
    isAdmin,
    openProjectOwnerDialog,
    updateParameterOptionsList,
    defaultProjectOwner,
    isOwnerDisabled,
    levelOfSpecifications,
    isLevelOfSpecificationConfirmationDialogOpen,
    isLevelOfSpecificationChangeConfirmed,
    setIsLevelOfSpecificationConfirmationDialogOpen,
    setIsLevelOfSpecificationChangeConfirmed,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const numberOfColumns = 2
    const { isEnablePreview, projectId, id, isDefault } = useReactiveVar(
        activeHeaderFooter
    )
    const { handleToggleMergeFieldPreview } = useToggleMergeFieldPreview()

    const {
        RemoveOwnerConfirmationDialog,
        openRemoveOwnerDialog,
    } = useRemoveOwnerDialog()

    // Determines if Best Practices can be used. When true, the Best Practices menu, icons and BP-related elements will be shown.
    const showBPSetting = useReactiveVar(showBestPracticeSetting)

    const [isFormDirty, setIsFormDirty] = React.useState<boolean>(false)
    const [isPageValidOnSave, setIsPageValidOnSave] = React.useState<boolean>(
        false
    )
    const [progress, setProgress] = React.useState<number>(0)

    const [createProjectInProgress, setCreateProjectProgress] = React.useState(
        false
    )
    const defaultFormStateDeepClone = JSON.parse(
        JSON.stringify(defaultFormState)
    )
    const [baseFormState, setBaseFormState] = React.useState<
        NewProjectFormProps
    >(defaultFormStateDeepClone)

    const [formState, setFormState] = React.useState<NewProjectFormProps>(
        defaultFormStateDeepClone
    )
    const [isStateAuto, setStateAuto] = React.useState(
        defaultFormStateDeepClone.country.value === 'United States'
    )

    // This state is used to enable/disable Parameter Delete Button
    const [
        disableParameterDeleteButton,
        setDisableParameterDeleteButton,
    ] = React.useState(false)

    const projectPhaseToLevelOfSpecificationMapping: Record<string, string> = {
        'Pre-Design': '100',
        'Schematic Design': '200',
        'Design Development': '300',
        'Construction Documents': '350',
        'Bidding & Negotiation': '400',
        'Construction Administration': '400',
        'Facilities Management': '500',
    }

    const formStateRef = React.useRef<NewProjectFormProps>(formState)

    useReactiveVar(ownerId)

    const dataArray = (dataObj: { [key: string]: string }) => {
        return Object.keys(dataObj).map((key) => {
            return { value: dataObj[key], name: dataObj[key] }
        })
    }

    // Determine the visibility of the "levelOfSpecification" field for existing projects
    const isLevelOfSpecificationVisible =
        isEditMode &&
        (formStateRef?.current?.levelOfSpecification?.value ||
            formStateRef?.current?.levelOfSpecification !== undefined) &&
        formState?.levelOfSpecification?.value !== undefined

    const updateUserFormState = updateFormState({
        state: formState,
        setState: setFormState,
    } as StateSetStateProps)

    const onTextFieldChange = (targetName: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        updateUserFormState({
            name: targetName,
            value: event.currentTarget.value,
        })
    }

    const handleChange = useCallback(
        (name: string, value: string | null | undefined) => {
            value = value || ''

            if (name.toLowerCase() === 'country') {
                setStateAuto(value === 'United States')
            }

            if (name.toLowerCase() === 'projectphase') {
                const phase = projectPhases.find(
                    (phase) => phase.value === value
                )
                const losValue = phase
                    ? projectPhaseToLevelOfSpecificationMapping[phase.name]
                    : ''

                const dateToday = new Date()

                // Create an object for the updates
                const updates = [
                    { name, value },
                    { name: 'levelOfSpecification', value: losValue },
                    {
                        name: 'phaseStartDate',
                        value: value === '' ? '' : formatLocalDate(dateToday),
                    },
                    { name: 'phaseEndDate', value: '' },
                ]

                // Update the form state immutably with all the changes
                updateUserFormState(...updates)
            } else {
                updateUserFormState({ name, value })
            }
        },
        [
            projectPhases,
            projectPhaseToLevelOfSpecificationMapping,
            updateUserFormState,
            setStateAuto,
            formatLocalDate,
        ]
    )

    const validateStartDateAndSetMessage = (): string | null => {
        if (!validateDate(formState.phaseStartDate.value, dateFormat)) {
            return 'Invalid date format.'
        }
        if (
            !isDateRangeValid(
                formState.phaseStartDate.value!,
                formState.phaseEndDate.value!,
                dateFormat
            )
        ) {
            return 'Date should not be after planned phase completion date.'
        }
        return null
    }

    const validateEndDateAndSetMessage = (): string | null => {
        if (!validateDate(formState.phaseEndDate.value, dateFormat)) {
            return 'Invalid date format.'
        }
        if (
            !isDateRangeValid(
                formState.phaseStartDate.value!,
                formState.phaseEndDate.value!,
                dateFormat
            )
        ) {
            return 'Date should not be before phase start date.'
        }
        return null
    }
    const handleDateChange = (
        date: Date | null,
        name: string,
        value: string | null | undefined
    ) => {
        value = value || ''

        // Format date to exclude timezone (store only YYYY-MM-DD)
        const formattedValue = formatLocalDate(date) || value
        const phaseEndDateFormattedValue = formState.phaseEndDate.value
            ? formatLocalDate(new Date(formState.phaseEndDate.value))
            : formState.phaseEndDate.value

        if (name === 'phaseStartDate') {
            updateUserFormState(
                {
                    name,
                    value: formattedValue,
                    valid:
                        validateDate(formattedValue, dateFormat) &&
                        isDateRangeValid(
                            formattedValue,
                            formState.phaseEndDate.value!,
                            dateFormat
                        ),
                },
                {
                    name: 'phaseEndDate',
                    value: phaseEndDateFormattedValue,
                    valid:
                        validateDate(
                            formState.phaseEndDate.value,
                            dateFormat
                        ) &&
                        isDateRangeValid(
                            formattedValue,
                            formState.phaseEndDate.value!,
                            dateFormat
                        ),
                }
            )
        } else if (name === 'phaseEndDate') {
            updateUserFormState(
                {
                    name,
                    value: formattedValue,
                    valid:
                        validateDate(formattedValue, dateFormat) &&
                        isDateRangeValid(
                            formState.phaseStartDate.value!,
                            formattedValue,
                            dateFormat
                        ),
                },
                {
                    name: 'phaseStartDate',
                    value: formState.phaseStartDate.value,
                    valid:
                        validateDate(
                            formState.phaseStartDate.value,
                            dateFormat
                        ) &&
                        isDateRangeValid(
                            formState.phaseStartDate.value!,
                            formattedValue,
                            dateFormat
                        ),
                }
            )
        } else if (name === 'projectDate' || name === 'issueDate') {
            let newDate = value
            let isDateValid = false
            if (date) {
                // Store only the date part (YYYY-MM-DD) to prevent timezone shifts
                newDate = formatLocalDate(date) || ''
                isDateValid = true
            } else if (newDate === '') {
                // Project Date and Issue Date fields are optional. Accept empty value
                isDateValid = true
            }
            updateUserFormState({
                name,
                value: newDate,
                valid: isDateValid,
            })
        } else {
            updateUserFormState({
                name,
                value,
                valid: true,
            })
        }
    }

    const handleConfirmationDialog = () => {
        continueSave()
        setIsLevelOfSpecificationChangeConfirmed?.(true)
        setIsLevelOfSpecificationConfirmationDialogOpen?.(false)
    }

    const handleConfirmationDialogClose = () => {
        if (!isLevelOfSpecificationChangeConfirmed) {
            updateUserFormState({
                name: 'levelOfSpecification',
                value: baseFormState?.levelOfSpecification.value,
            })
        }
        setIsLevelOfSpecificationChangeConfirmed?.(false)
        setIsLevelOfSpecificationConfirmationDialogOpen?.(false)
    }

    const continueSave = React.useCallback(async () => {
        setIsPageValidOnSave(true)

        let mileStone: MilestoneInput | undefined = undefined

        if (formStateRef.current.projectPhase.value! !== '') {
            mileStone = {
                endDate:
                    formStateRef.current.phaseEndDate.value! !== ''
                        ? formStateRef.current.phaseEndDate.value!
                        : null,
                phaseId: formStateRef.current.projectPhase.value!,
                startDate:
                    formStateRef.current.phaseStartDate.value! !== ''
                        ? formStateRef.current.phaseStartDate.value!
                        : null,
            }
        }
        const address: Partial<Address> = {
            street1: formStateRef.current.street1.value!,
            street2: formStateRef.current.street2.value!,
            city: formStateRef.current.city.value!,
            region: formStateRef.current.region.value!,
            country: formStateRef.current.country.value!,
            postalCode: formStateRef.current.postalCode.value!,
        }

        const projectParamters = associationModifierLists.map(
            (associationModifierList) => {
                return {
                    modifierListId: associationModifierList.value,
                    selectedModifierIds: formStateRef.current[
                        associationModifierList.value as string
                    ].value!.split(','),
                } as ParameterInput
            }
        )

        const projectInput: ProjectInput = {
            firmId: firmId,
            budget: formStateRef.current.constructionBudget.value!,
            groupId: formStateRef.current.projectGroup.value!,
            grossBuildingArea: formStateRef.current.grossBuildingArea.value!,
            location: address,
            name: formStateRef.current.projectName.value!,
            number: formStateRef.current.projectNumber.value!,
            projectParameters: projectParamters,
            initialMilestone: !isEditMode ? mileStone : undefined,
            levelOfSpecification: formStateRef.current.levelOfSpecification
                .value!,
            autoAddBestPractice: !!showBPSetting,
        }

        if (!isEditMode) {
            await createProject({
                variables: { createProjectInput: projectInput },
            })
        } else {
            await updateProject({
                variables: {
                    updateProjectInput: {
                        ...projectInput,
                        id: formStateRef.current.id.value!,
                        addendumNumber:
                            formStateRef.current.addendumNumber.value,
                        projectDate: !!formStateRef.current.projectDate.value
                            ? new Date(formStateRef.current.projectDate.value)
                            : undefined,
                        issueDate: !!formStateRef.current.issueDate.value
                            ? new Date(formStateRef.current.issueDate.value)
                            : undefined,
                        issueFor: formStateRef.current.issueFor.value,
                    },
                },
            })

            //Update header and footer template
            if (isEnablePreview && !isDefault) {
                const toggleMergeFieldPreviewInput: ToggleMergeFieldPreviewInput = {
                    projectHeaderAndFooterId: id ?? '',
                    projectId: projectId ?? '',
                    firmId: firmId,
                    isEnablePreview: true,
                }
                await handleToggleMergeFieldPreview(
                    toggleMergeFieldPreviewInput,
                    setHeaderMergeFields,
                    setFooterMergeFields
                )
            }
        }
    }, [
        formStateRef,
        associationModifierLists,
        createProject,
        updateProject,
        handleToggleMergeFieldPreview,
        setHeaderMergeFields,
        setFooterMergeFields,
        id,
        projectId,
        isEnablePreview,
        isDefault,
        firmId,
        showBPSetting,
        isEditMode,
    ])

    const onBtnSave = React.useCallback(() => {
        const newFormState = { ...formStateRef.current }

        if (isEditMode && !isLevelOfSpecificationVisible) {
            newFormState.levelOfSpecification = {
                ...newFormState.levelOfSpecification,
                required: false,
                valid: true,
            }
        }

        const isPageValid = validateFormState({
            state: newFormState,
            setState: (updatedFormState) => {
                setFormState(updatedFormState)
            },
        })

        if (isPageValid) {
            if (
                isEditMode &&
                isLevelOfSpecificationVisible &&
                formStateRef.current.levelOfSpecification.value !==
                    baseFormState.levelOfSpecification.value
            ) {
                setIsLevelOfSpecificationConfirmationDialogOpen?.(true)
            } else {
                continueSave()
            }
        }
    }, [formStateRef.current])

    /**
     * Handles form state and parameter list updates after successful owner deletion.
     *
     * @param {string} ownerId - The ID of the deleted owner
     *
     */
    const updateListAfterOwnerDeletion = useCallback(
        (ownerId: string) => {
            const ownerParameterOptions = associationModifierLists.find(
                (x) => x.name === 'Owner'
            )
            if (
                ownerParameterOptions == null ||
                updateParameterOptionsList == null
            )
                return

            // Check if the deleted owner was selected in the form
            const ownerModifierListId = ownerParameterOptions.value as string
            const currentValue = formState[ownerModifierListId]?.value
            if (currentValue === ownerId) {
                // Clear the selection if the deleted owner was selected
                handleChange(ownerParameterOptions.value as string, undefined)
            }

            // Update parameter lists
            updateParameterOptionsList(ownerId, ownerModifierListId)
            setDisableParameterDeleteButton(false)
        },
        [
            associationModifierLists,
            updateParameterOptionsList,
            formState,
            updateUserFormState,
        ]
    )

    /**
     * Initiates the owner deletion workflow by opening the confirmation dialog.
     *
     * @param {string} ownerId - The unique identifier of the owner to be deleted
     *
     * @remarks
     * This method serves as the entry point for owner deletion from the UI.
     * It opens a confirmation dialog and registers a callback to handle post-deletion updates.
     */
    const openRemoveOwnerDialogWithCallback = useCallback(
        (
            ownerId: string,
            onSuccessCallback?: () => void,
            onCancelCallback?: () => void
        ) => {
            setDisableParameterDeleteButton(true)
            openRemoveOwnerDialog(
                ownerId,
                () => {
                    updateListAfterOwnerDeletion(ownerId)
                    onSuccessCallback?.()
                },
                () => {
                    setDisableParameterDeleteButton(false)
                    onCancelCallback?.()
                }
            )
        },
        [openRemoveOwnerDialog, updateListAfterOwnerDeletion]
    )

    const createProjectParamterColumn = (rowIndex: number) => {
        const columns: any[] = []
        for (let index = 0; index < numberOfColumns; index++) {
            const modifierLists =
                associationModifierLists[rowIndex * numberOfColumns + index]
            if (!!modifierLists) {
                const name = modifierLists.name as string,
                    value = modifierLists.value as string,
                    multiple = isParameterMultiSelect({
                        name,
                        multiple: modifierLists.multiple as boolean,
                    })

                let projectParameterValue: string | null = formState[value]
                    .value as string
                const parameterOptions = getModifiers(value) ?? []

                // Ensure the current parameter value is still present in the available options; if not, reset to null
                // Skip validation for multi-select fields (they contain comma-separated values)
                if (!multiple) {
                    projectParameterValue =
                        parameterOptions.find(
                            (x) => x.value === projectParameterValue
                        )?.value ?? null
                }

                if (!multiple) {
                    if (name !== 'Owner') {
                        columns.push(
                            <GridItem
                                key={value}
                                xs={6}
                                className={
                                    index === 0
                                        ? classes.firstColumnPadding
                                        : undefined
                                }
                            >
                                <FormControlOutlined
                                    additionalClassNames={classes.formControl}
                                    data-testid={`dv${value}`}
                                >
                                    <ProjectInputSingle
                                        options={parameterOptions}
                                        value={projectParameterValue}
                                        name={value}
                                        placeholder={`Select a ${name}`}
                                        label={name}
                                        required={formState[value].required}
                                        error={hasError(formState[value])}
                                        handleChange={handleChange}
                                        htmlAutoCmpltDisabled
                                    />
                                </FormControlOutlined>
                            </GridItem>
                        )
                    } else {
                        updateOwnerId(value)
                        columns.push(
                            <GridItem
                                key={value}
                                xs={6}
                                className={
                                    index === 0
                                        ? classes.firstColumnPadding
                                        : undefined
                                }
                            >
                                <FormControlOutlined
                                    additionalClassNames={classes.formControl}
                                    data-testid={`dv${value}`}
                                >
                                    <ProjectInputSingle
                                        options={sortOptionsAlphabetically(
                                            parameterOptions
                                        )}
                                        value={projectParameterValue}
                                        name={value}
                                        placeholder={`Add or Select ${name}`}
                                        label={name}
                                        required={formState[value].required}
                                        error={hasError(formState[value])}
                                        handleChange={handleChange}
                                        openProjectOwnerDialog={
                                            openProjectOwnerDialog
                                        }
                                        isAdmin={isAdmin}
                                        isOwnerDisabled={isOwnerDisabled}
                                        htmlAutoCmpltDisabled
                                        openRemoveParameterDialog={
                                            openRemoveOwnerDialogWithCallback
                                        }
                                        disable={disableParameterDeleteButton}
                                    />
                                </FormControlOutlined>
                            </GridItem>
                        )
                    }
                } else {
                    columns.push(
                        <GridItem
                            key={value}
                            xs={6}
                            className={
                                index === 0
                                    ? classes.firstColumnPadding
                                    : undefined
                            }
                        >
                            <FormControlOutlined
                                additionalClassNames={classes.formControl}
                                data-testid={`dv${value}`}
                            >
                                <ProjectInputMulti
                                    options={parameterOptions}
                                    value={projectParameterValue}
                                    name={value}
                                    placeholder={`Select ${name}`}
                                    label={name}
                                    required={formState[value].required}
                                    error={hasError(formState[value])}
                                    handleChange={handleChange}
                                    htmlAutoCmpltDisabled
                                    inputType={name}
                                />
                            </FormControlOutlined>
                        </GridItem>
                    )
                }
            }
        }
        return <>{columns}</>
    }
    const createProjectParamterRow = () => {
        const rows: any[] = []
        const numberOfRows = Math.ceil(
            associationModifierLists.length / numberOfColumns
        )

        if (
            associationModifierLists.some(
                (associationModifierList) =>
                    !!formState[associationModifierList.value as string]
            )
        ) {
            for (let index = 0; index < numberOfRows; index++) {
                rows.push(
                    <GridContainer
                        key={`associationModifiers_${index}`}
                        spacing={3}
                        className={clsx(
                            classes.marginUnset,
                            classes.row,
                            classes.paddingLeft
                        )}
                    >
                        {createProjectParamterColumn(index)}
                    </GridContainer>
                )
            }
        }
        return rows
    }

    React.useEffect(() => {
        if (
            Object.keys(defaultFormState).length !==
            Object.keys(baseFormState).length
        ) {
            setBaseFormState(JSON.parse(JSON.stringify(defaultFormState)))
            setFormState(JSON.parse(JSON.stringify(defaultFormState)))
        }
    }, [defaultFormState, baseFormState])
    React.useEffect(() => {
        if (isPageValidOnSave) {
            setIsFormDirty(false)
            toggleFormDirty(false)
            return
        }

        const isCurrentlyDirty = isFormValueDirty({
            prevFormState: baseFormState,
            currentFormState: formState,
        })
        if (isFormDirty !== isCurrentlyDirty) {
            setIsFormDirty(isCurrentlyDirty)
            toggleFormDirty(isCurrentlyDirty)
        }
        formStateRef.current = formState
    }, [
        formState,
        isFormDirty,
        isPageValidOnSave,
        defaultFormState,
        toggleFormDirty,
        baseFormState,
    ])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (firmProjectGroupError) {
            setIsPageValidOnSave(false)

            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        firmProjectGroupError,
                        'Error in fetching project group for new project.'
                    ),
                    variant: 'error',
                    id: 'projectGroupDataLoadingError',
                },
            })
        }

        if (createProjectError) {
            setIsPageValidOnSave(false)

            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        createProjectError,
                        'Error in saving new project.'
                    ),
                    variant: 'error',
                    id: 'projectCreationError',
                },
            })
        }

        if (updateProjectError) {
            setIsPageValidOnSave(false)

            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        updateProjectError,
                        'Error in updating project.'
                    ),
                    variant: 'error',
                    id: 'projectUpdateError',
                },
            })
        }
    }, [
        firmProjectGroupError,
        createProjectError,
        updateProjectError,
        showGlobalSnackbar,
    ])

    const [getStatus, { data: statusData, error: statusError }] = useLazyQuery<{
        getProjectBulkAddStatus: Partial<BackendProcessStatus>
    }>(GetProjectBulkAddStatus, {
        variables: {
            input: {
                instanceId:
                    createProjectData?.createProject?.startBackendProcessResult
                        ?.instanceId,
            },
        },
        onCompleted: (data) => {
            setProgress(data?.getProjectBulkAddStatus?.progressPercentage ?? 0)
        },

        fetchPolicy: 'no-cache',
    })
    const handleCreateProjectCompleted = React.useCallback(() => {
        showGlobalSnackbar({
            variables: {
                message: `Project created successfully.`,
                variant: 'success',
                id: `ProjectCreationSuccess`,
            },
        })

        setBaseFormState(JSON.parse(JSON.stringify(defaultFormState)))
        setFormState(JSON.parse(JSON.stringify(defaultFormState)))

        if (triggerProjectListReload) {
            triggerProjectListReload()
            setSkipCount?.(0)
        }

        navigateToExternalLink(
            `/projects/${createProjectData?.createProject?.project?.id}/overview`,
            true
        )
        if (onCancel) onCancel()
    }, [createProjectData, showGlobalSnackbar])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (
            createProjectData &&
            createProjectData.createProject?.startBackendProcessResult === null
        ) {
            handleCreateProjectCompleted()
        } else if (
            !!createProjectData &&
            createProjectData.createProject?.startBackendProcessResult
                ?.instanceId &&
            !statusData &&
            !createProjectInProgress
        ) {
            setCreateProjectProgress(true)
            getStatus()
        }
    }, [createProjectInProgress, createProjectData, statusData])
    React.useEffect(() => {
        if (!statusData && !statusError) return
        if (statusData?.getProjectBulkAddStatus?.state === 'Complete') {
            setCreateProjectProgress(false)
            handleCreateProjectCompleted()
            setProgress(0)
        } else {
            //if project creation still in progress; poll again after 3 seconds time
            const queryTimeout = setTimeout(() => {
                getStatus()
                clearTimeout(queryTimeout)
            }, 3000)
        }
    }, [statusData, statusError])
    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (!!updateProjectData && updateProjectData.updateProject) {
            showGlobalSnackbar({
                variables: {
                    message: `Project updated successfully.`,
                    variant: 'success',
                    id: `ProjectUpdateSuccess`,
                },
            })

            setBaseFormState(JSON.parse(JSON.stringify(defaultFormState)))
            setFormState(JSON.parse(JSON.stringify(defaultFormState)))

            if (triggerProjectListReload) triggerProjectListReload()

            if (onCancel) onCancel()
        }
    }, [
        isFormDirty,
        updateProjectData,
        showGlobalSnackbar,
        defaultFormState,
        onCancel,
        triggerProjectListReload,
    ])

    React.useEffect(() => {
        if (defaultProjectGroup) {
            updateUserFormState({
                name: 'projectGroup',
                value: defaultProjectGroup?.value,
            })
        }
    }, [defaultProjectGroup])

    React.useEffect(() => {
        if (defaultProjectOwner) {
            updateUserFormState({
                name: ownerId(),
                value: defaultProjectOwner,
            })
        }
    }, [defaultProjectOwner])

    return (
        <>
            <div className={classes.wrap} data-testid="dvNewProjectModal">
                <GridContainer className={classes.header}>
                    <GridItem
                        className={clsx(
                            classes.headerText,
                            classes.paddingLeft
                        )}
                    >
                        {isEditMode
                            ? 'Edit Project Details'
                            : 'New Project Details'}
                    </GridItem>
                </GridContainer>
                <GridContainer className={classes.container}>
                    <GridItem xs={12}>
                        <div data-testid="dvProjectDetails">
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.newProjectFormInputTop,
                                    classes.row,
                                    classes.paddingLeft
                                )}
                            >
                                <GridItem
                                    xs={6}
                                    className={classes.firstColumnPadding}
                                >
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvProjectGroup"
                                    >
                                        <ProjectInputSingle
                                            options={sortOptionsAlphabetically(
                                                projectGroups
                                            )}
                                            value={
                                                formState.projectGroup.value ??
                                                ''
                                            }
                                            name="projectGroup"
                                            placeholder="Select Project Group"
                                            label="Project Group"
                                            required={
                                                formState.projectGroup.required
                                            }
                                            error={hasError(
                                                formState.projectGroup
                                            )}
                                            handleChange={handleChange}
                                            openProjectGroupDialog={
                                                openProjectGroupDialog
                                            }
                                            isAdmin={isAdmin}
                                            htmlAutoCmpltDisabled
                                        />
                                    </FormControlOutlined>
                                </GridItem>
                                <GridItem xs={6}>
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvProjectNumber"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={
                                                formState.projectNumber.required
                                            }
                                            label="Project Number"
                                            variant="outlined"
                                            error={hasError(
                                                formState.projectNumber
                                            )}
                                            value={
                                                formState.projectNumber.value ??
                                                ''
                                            }
                                            onChange={onTextFieldChange(
                                                'projectNumber'
                                            )}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                            }}
                                            inputProps={{
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                                'aria-label': 'project number',
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>
                            </GridContainer>
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row,
                                    classes.paddingLeft
                                )}
                            >
                                <GridItem
                                    xs={12}
                                    className={classes.firstColumnPadding}
                                >
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvProjectName"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={
                                                formState.projectName.required
                                            }
                                            label="Project Name"
                                            value={
                                                formState.projectName.value ??
                                                ''
                                            }
                                            variant="outlined"
                                            error={hasError(
                                                formState.projectName
                                            )}
                                            onChange={onTextFieldChange(
                                                'projectName'
                                            )}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                            }}
                                            inputProps={{
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                                'aria-label': 'project name',
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>
                            </GridContainer>
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row,
                                    classes.paddingLeft
                                )}
                            >
                                <GridItem
                                    xs={6}
                                    className={classes.firstColumnPadding}
                                >
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvConstructionBudget"
                                    >
                                        <ProjectInputSingle
                                            options={
                                                constructionBudgets as Record<
                                                    nameValuePropType,
                                                    string
                                                >[]
                                            }
                                            value={
                                                formState.constructionBudget
                                                    .value ?? ''
                                            }
                                            label="Construction Budget"
                                            placeholder="Select a Construction Budget"
                                            name="constructionBudget"
                                            required={
                                                formState.constructionBudget
                                                    .required
                                            }
                                            error={hasError(
                                                formState.constructionBudget
                                            )}
                                            handleChange={handleChange}
                                            htmlAutoCmpltDisabled
                                        />
                                    </FormControlOutlined>
                                </GridItem>
                                <GridItem xs={6}>
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvGrossBuildingArea"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={
                                                formState.grossBuildingArea
                                                    .required
                                            }
                                            label="Gross Building Area"
                                            value={
                                                formState.grossBuildingArea
                                                    .value ?? ''
                                            }
                                            variant="outlined"
                                            error={hasError(
                                                formState.grossBuildingArea
                                            )}
                                            onChange={onTextFieldChange(
                                                'grossBuildingArea'
                                            )}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                                'aria-label':
                                                    'gross building area',
                                            }}
                                            inputProps={{
                                                'aria-label':
                                                    'gross building area',
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>
                            </GridContainer>
                        </div>
                        {isEditMode && (
                            <>
                                <GridContainer
                                    spacing={3}
                                    className={clsx(
                                        classes.marginUnset,
                                        classes.row,
                                        classes.paddingLeft
                                    )}
                                >
                                    <MuiPickersUtilsProvider
                                        utils={DateFnsUtils}
                                    >
                                        <GridItem
                                            xs={6}
                                            className={
                                                classes.firstColumnPadding
                                            }
                                        >
                                            <KeyboardDatePicker
                                                size="small"
                                                data-testid={
                                                    'dvFormProjectDate'
                                                }
                                                disableToolbar
                                                format={dateFormat}
                                                autoOk
                                                label="Project Date"
                                                name="projectDate"
                                                value={
                                                    !!formState.projectDate
                                                        .value
                                                        ? formatDateWithTimezone(
                                                              formState
                                                                  .projectDate
                                                                  .value
                                                          )
                                                        : null
                                                }
                                                required={false}
                                                onChange={(
                                                    date: Date | null,
                                                    value:
                                                        | string
                                                        | null
                                                        | undefined
                                                ) =>
                                                    handleDateChange(
                                                        date,
                                                        'projectDate',
                                                        value
                                                    )
                                                }
                                                inputVariant="outlined"
                                                variant="inline"
                                                PopoverProps={{
                                                    style: { zIndex: 1302 },
                                                }}
                                                inputProps={{
                                                    'aria-label':
                                                        'Project Date',
                                                }}
                                                KeyboardButtonProps={{
                                                    edge: 'end',
                                                    'aria-label':
                                                        'date picker button',
                                                }}
                                                InputAdornmentProps={{
                                                    className:
                                                        classes.dateAdornment,
                                                }}
                                                TextFieldComponent={
                                                    CalendarTextField
                                                }
                                                error={
                                                    !validateDate(
                                                        formState.projectDate
                                                            .value
                                                            ? new Date(
                                                                  formState.projectDate.value
                                                              ).toISOString()
                                                            : '',
                                                        "yyyy-MM-dd'T'HH:mm:ss.SSSX"
                                                    ) ||
                                                    hasError(
                                                        formState.projectDate
                                                    )
                                                }
                                            />
                                        </GridItem>
                                    </MuiPickersUtilsProvider>

                                    <GridItem xs={6}>
                                        <FormControlOutlined
                                            additionalClassNames={
                                                classes.formControl
                                            }
                                            data-testid="dvFormIssueFor"
                                        >
                                            <TextField
                                                size="small"
                                                fullWidth={true}
                                                required={
                                                    formState?.issueFor
                                                        ?.required ?? false
                                                }
                                                label="Issue For"
                                                value={
                                                    formState?.issueFor
                                                        ?.value ?? ''
                                                }
                                                variant="outlined"
                                                onChange={onTextFieldChange(
                                                    'issueFor'
                                                )}
                                                inputProps={{
                                                    'aria-label': 'Issue For',
                                                }}
                                                InputLabelProps={{
                                                    className: clsx(
                                                        classes.text,
                                                        classes.selectLabel
                                                    ),
                                                }}
                                                InputProps={{
                                                    classes: {
                                                        input: classes.text,
                                                        root: classes.root,
                                                    },
                                                }}
                                            ></TextField>
                                        </FormControlOutlined>
                                    </GridItem>
                                </GridContainer>
                                <GridContainer
                                    spacing={3}
                                    className={clsx(
                                        classes.marginUnset,
                                        classes.row,
                                        classes.paddingLeft
                                    )}
                                >
                                    <MuiPickersUtilsProvider
                                        utils={DateFnsUtils}
                                    >
                                        <GridItem
                                            xs={6}
                                            className={
                                                classes.firstColumnPadding
                                            }
                                        >
                                            <KeyboardDatePicker
                                                size="small"
                                                data-testid={'dvFormIssueDate'}
                                                disableToolbar
                                                format={dateFormat}
                                                autoOk
                                                label="Issue Date"
                                                name="issueDate"
                                                value={
                                                    !!formState.issueDate.value
                                                        ? formatDateWithTimezone(
                                                              formState
                                                                  .issueDate
                                                                  .value
                                                          )
                                                        : null
                                                }
                                                required={false}
                                                onChange={(
                                                    date: Date | null,
                                                    value:
                                                        | string
                                                        | null
                                                        | undefined
                                                ) =>
                                                    handleDateChange(
                                                        date,
                                                        'issueDate',
                                                        value
                                                    )
                                                }
                                                inputVariant="outlined"
                                                variant="inline"
                                                PopoverProps={{
                                                    style: { zIndex: 1302 },
                                                }}
                                                inputProps={{
                                                    'aria-label': 'Issue Date',
                                                }}
                                                KeyboardButtonProps={{
                                                    edge: 'end',
                                                    'aria-label':
                                                        'date picker button',
                                                }}
                                                InputAdornmentProps={{
                                                    className:
                                                        classes.dateAdornment,
                                                }}
                                                TextFieldComponent={
                                                    CalendarTextField
                                                }
                                                error={
                                                    !validateDate(
                                                        formState.issueDate
                                                            .value
                                                            ? new Date(
                                                                  formState.issueDate.value
                                                              ).toISOString()
                                                            : '',
                                                        "yyyy-MM-dd'T'HH:mm:ss.SSSX"
                                                    ) ||
                                                    hasError(
                                                        formState.issueDate
                                                    )
                                                }
                                            />
                                        </GridItem>
                                    </MuiPickersUtilsProvider>
                                    <GridItem xs={6}>
                                        <FormControlOutlined
                                            additionalClassNames={
                                                classes.formControl
                                            }
                                            data-testid="dvFormAddendumNumber"
                                        >
                                            <TextField
                                                size="small"
                                                fullWidth={true}
                                                required={
                                                    formState?.addendumNumber
                                                        ?.required ?? false
                                                }
                                                label="Addendum Number"
                                                value={
                                                    formState?.addendumNumber
                                                        ?.value ?? ''
                                                }
                                                variant="outlined"
                                                onChange={onTextFieldChange(
                                                    'addendumNumber'
                                                )}
                                                inputProps={{
                                                    'aria-label':
                                                        'Addendum Number',
                                                }}
                                                InputLabelProps={{
                                                    className: clsx(
                                                        classes.text,
                                                        classes.selectLabel
                                                    ),
                                                }}
                                                InputProps={{
                                                    classes: {
                                                        input: classes.text,
                                                        root: classes.root,
                                                    },
                                                }}
                                            ></TextField>
                                        </FormControlOutlined>
                                    </GridItem>
                                </GridContainer>
                                {isLevelOfSpecificationVisible && (
                                    <GridContainer
                                        spacing={3}
                                        className={clsx(
                                            classes.marginUnset,
                                            classes.row,
                                            classes.paddingLeft
                                        )}
                                    >
                                        <GridItem
                                            xs={6}
                                            className={
                                                classes.firstColumnPadding
                                            }
                                        >
                                            <FormControlOutlined
                                                additionalClassNames={
                                                    classes.formControl
                                                }
                                                data-testid="dvLevelOfSpecificationEdit"
                                            >
                                                <ProjectInputSingle
                                                    options={
                                                        levelOfSpecifications
                                                    }
                                                    value={
                                                        formState
                                                            .levelOfSpecification
                                                            .value || ''
                                                    }
                                                    name="levelOfSpecification"
                                                    placeholder="Select Level of Specification"
                                                    label="Level of Specification"
                                                    required={
                                                        isLevelOfSpecificationVisible
                                                    }
                                                    error={hasError(
                                                        formState.levelOfSpecification
                                                    )}
                                                    handleChange={handleChange}
                                                    htmlAutoCmpltDisabled
                                                />
                                            </FormControlOutlined>
                                        </GridItem>
                                    </GridContainer>
                                )}
                            </>
                        )}
                        <div data-testid="dvProjectLocation">
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row
                                )}
                            >
                                <GridItem
                                    xs={12}
                                    className={clsx(
                                        classes.paddingLeft,
                                        classes.dividerHeading
                                    )}
                                >
                                    PROJECT LOCATION
                                </GridItem>
                                <GridItem
                                    xs={12}
                                    className={clsx(classes.dividerContainer)}
                                >
                                    <Divider
                                        variant="fullWidth"
                                        className={clsx(classes.divider)}
                                    />
                                </GridItem>
                            </GridContainer>
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row,
                                    classes.paddingLeft
                                )}
                            >
                                <GridItem
                                    xs={6}
                                    className={classes.firstColumnPadding}
                                >
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvAddress"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={
                                                formState.street1.required
                                            }
                                            label="Address"
                                            variant="outlined"
                                            error={hasError(formState.street1)}
                                            value={
                                                formState.street1.value || ''
                                            }
                                            onChange={onTextFieldChange(
                                                'street1'
                                            )}
                                            inputProps={{
                                                maxLength:
                                                    formState.street1.maxLength,
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                                'aria-label': 'address',
                                            }}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>
                                <GridItem xs={6}>
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvAddress2"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={
                                                formState.street2.required
                                            }
                                            value={
                                                formState.street2.value || ''
                                            }
                                            label="Address 2"
                                            variant="outlined"
                                            error={hasError(formState.street2)}
                                            onChange={onTextFieldChange(
                                                'street2'
                                            )}
                                            inputProps={{
                                                maxLength:
                                                    formState.street2.maxLength,
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                                'aria-label': 'address 2',
                                            }}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>
                            </GridContainer>
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row,
                                    classes.paddingLeft
                                )}
                            >
                                <GridItem
                                    xs={6}
                                    className={classes.firstColumnPadding}
                                >
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvCity"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={formState.city.required}
                                            label="City"
                                            variant="outlined"
                                            value={formState.city.value || ''}
                                            error={hasError(formState.city)}
                                            onChange={onTextFieldChange('city')}
                                            inputProps={{
                                                maxLength:
                                                    formState.city.maxLength,
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                                'aria-label': 'city',
                                            }}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>
                                <GridItem xs={6}>
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvState"
                                    >
                                        <ProjectInputSingle
                                            options={
                                                (isStateAuto &&
                                                    dataArray(States)) ||
                                                []
                                            }
                                            value={formState.region.value || ''}
                                            name="region"
                                            required={formState.region.required}
                                            error={hasError(formState.region)}
                                            label="State/Province"
                                            placeholder=""
                                            handleChange={handleChange}
                                            maxLength={
                                                formState.region.maxLength
                                            }
                                            htmlAutoCmpltDisabled
                                        />
                                    </FormControlOutlined>
                                </GridItem>
                            </GridContainer>
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row,
                                    classes.paddingLeft
                                )}
                            >
                                <GridItem
                                    xs={6}
                                    className={classes.firstColumnPadding}
                                >
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvZip"
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth={true}
                                            required={
                                                formState.postalCode.required
                                            }
                                            label="Zip"
                                            variant="outlined"
                                            error={hasError(
                                                formState.postalCode
                                            )}
                                            onChange={onTextFieldChange(
                                                'postalCode'
                                            )}
                                            value={
                                                formState.postalCode.value || ''
                                            }
                                            inputProps={{
                                                maxLength:
                                                    formState.postalCode
                                                        .maxLength,
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                                'aria-label': 'zip',
                                            }}
                                            InputLabelProps={{
                                                className: clsx(
                                                    classes.text,
                                                    classes.selectLabel
                                                ),
                                            }}
                                            InputProps={{
                                                classes: {
                                                    input: classes.text,
                                                    root: classes.root,
                                                },
                                            }}
                                        ></TextField>
                                    </FormControlOutlined>
                                </GridItem>

                                <GridItem xs={6}>
                                    <FormControlOutlined
                                        additionalClassNames={
                                            classes.formControl
                                        }
                                        data-testid="dvCountry"
                                    >
                                        <ProjectInputSingle
                                            options={dataArray(Countries)}
                                            value={
                                                formState.country.value || ''
                                            }
                                            name="country"
                                            label="Country"
                                            placeholder=""
                                            required={
                                                formState.country.required
                                            }
                                            error={hasError(formState.country)}
                                            handleChange={handleChange}
                                            htmlAutoCmpltDisabled
                                        />
                                    </FormControlOutlined>
                                </GridItem>
                            </GridContainer>
                        </div>
                        <div data-testid="dvProjectParameter">
                            <GridContainer
                                spacing={3}
                                className={clsx(
                                    classes.marginUnset,
                                    classes.row
                                )}
                            >
                                <GridItem
                                    xs={12}
                                    className={clsx(
                                        classes.paddingLeft,
                                        classes.dividerHeading
                                    )}
                                >
                                    PROJECT PARAMETERS
                                </GridItem>
                                <GridItem
                                    xs={12}
                                    className={clsx(classes.dividerContainer)}
                                >
                                    <Divider
                                        variant="fullWidth"
                                        className={clsx(classes.divider)}
                                    />
                                </GridItem>
                            </GridContainer>
                            {createProjectParamterRow()}
                        </div>
                        <div data-testid="dvProjectMilestone">
                            {!isEditMode && (
                                <>
                                    <GridContainer
                                        spacing={3}
                                        className={clsx(
                                            classes.marginUnset,
                                            classes.row
                                        )}
                                    >
                                        <GridItem
                                            xs={12}
                                            className={clsx(
                                                classes.paddingLeft,
                                                classes.dividerHeading
                                            )}
                                        >
                                            INITIAL PROJECT MILESTONE
                                        </GridItem>
                                        <GridItem
                                            xs={12}
                                            className={clsx(
                                                classes.dividerContainer
                                            )}
                                        >
                                            <Divider
                                                variant="fullWidth"
                                                className={clsx(
                                                    classes.divider
                                                )}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                    <MuiPickersUtilsProvider
                                        utils={DateFnsUtils}
                                    >
                                        <GridContainer
                                            spacing={3}
                                            className={clsx(
                                                classes.marginUnset,
                                                classes.row,
                                                classes.paddingLeft
                                            )}
                                        >
                                            <GridItem
                                                xs={6}
                                                className={
                                                    classes.firstColumnPadding
                                                }
                                            >
                                                <FormControlOutlined
                                                    additionalClassNames={
                                                        classes.formControl
                                                    }
                                                    data-testid="dvProjectPhase"
                                                >
                                                    <ProjectInputSingle
                                                        options={projectPhases}
                                                        value={
                                                            formState
                                                                .projectPhase
                                                                .value || ''
                                                        }
                                                        name="projectPhase"
                                                        placeholder="Select a Phase"
                                                        label="Design Phase"
                                                        required={
                                                            formState
                                                                .projectPhase
                                                                .required
                                                        }
                                                        error={hasError(
                                                            formState.projectPhase
                                                        )}
                                                        handleChange={
                                                            handleChange
                                                        }
                                                        htmlAutoCmpltDisabled
                                                    />
                                                </FormControlOutlined>
                                            </GridItem>
                                            <GridItem
                                                xs={6}
                                                className={
                                                    classes.firstColumnPadding
                                                }
                                            >
                                                <FormControlOutlined
                                                    additionalClassNames={
                                                        classes.formControl
                                                    }
                                                    data-testid="dvLevelOfSpecification"
                                                >
                                                    <ProjectInputSingle
                                                        options={
                                                            levelOfSpecifications
                                                        }
                                                        value={
                                                            formState
                                                                .levelOfSpecification
                                                                .value || ''
                                                        }
                                                        name="levelOfSpecification"
                                                        placeholder="Select Level of Specification"
                                                        label="Level of Specification"
                                                        required={
                                                            formState
                                                                .levelOfSpecification
                                                                .required
                                                        }
                                                        disable={
                                                            !formState
                                                                .projectPhase
                                                                .value
                                                        }
                                                        error={hasError(
                                                            formState.levelOfSpecification
                                                        )}
                                                        handleChange={
                                                            handleChange
                                                        }
                                                        htmlAutoCmpltDisabled
                                                    />
                                                </FormControlOutlined>
                                            </GridItem>
                                        </GridContainer>
                                        <GridContainer
                                            spacing={3}
                                            className={clsx(
                                                classes.marginUnset,
                                                classes.row,
                                                classes.paddingLeft
                                            )}
                                        >
                                            <GridItem
                                                xs={6}
                                                className={
                                                    classes.firstColumnPadding
                                                }
                                            >
                                                <KeyboardDatePicker
                                                    size="small"
                                                    data-testid={
                                                        'dvFromPhaseStartDate'
                                                    }
                                                    disabled={
                                                        (formState.projectPhase
                                                            .value || '') === ''
                                                    }
                                                    disableToolbar
                                                    format={dateFormat}
                                                    autoOk
                                                    label="Phase Start Date"
                                                    helperText={validateStartDateAndSetMessage()}
                                                    error={
                                                        !(
                                                            validateDate(
                                                                formState
                                                                    .phaseStartDate
                                                                    .value,
                                                                dateFormat
                                                            ) &&
                                                            isDateRangeValid(
                                                                formState
                                                                    .phaseStartDate
                                                                    .value!,
                                                                formState
                                                                    .phaseEndDate
                                                                    .value!,
                                                                dateFormat
                                                            )
                                                        ) ||
                                                        hasError(
                                                            formState.phaseStartDate
                                                        )
                                                    }
                                                    name="phaseStartDate"
                                                    maxDate={
                                                        formState.phaseEndDate
                                                            .value === ''
                                                            ? null
                                                            : formState
                                                                  .phaseEndDate
                                                                  .value
                                                    }
                                                    value={
                                                        formState.phaseStartDate
                                                            .value === ''
                                                            ? null
                                                            : formatDateWithTimezone(
                                                                  formState
                                                                      .phaseStartDate
                                                                      .value ||
                                                                      ''
                                                              )
                                                    }
                                                    required={
                                                        formState.phaseStartDate
                                                            .required
                                                    }
                                                    onChange={(
                                                        date: Date | null,
                                                        value:
                                                            | string
                                                            | null
                                                            | undefined
                                                    ) =>
                                                        handleDateChange(
                                                            date,
                                                            'phaseStartDate',
                                                            value
                                                        )
                                                    }
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    PopoverProps={{
                                                        style: { zIndex: 1302 },
                                                    }}
                                                    inputProps={{
                                                        'aria-label':
                                                            'phase start date',
                                                    }}
                                                    KeyboardButtonProps={{
                                                        edge: 'end',
                                                        'aria-label':
                                                            'date picker button',
                                                    }}
                                                    InputAdornmentProps={{
                                                        className:
                                                            classes.dateAdornment,
                                                    }}
                                                    TextFieldComponent={
                                                        CalendarTextField
                                                    }
                                                    leftArrowButtonProps={{
                                                        'aria-label':
                                                            'previous month',
                                                    }}
                                                    rightArrowButtonProps={{
                                                        'aria-label':
                                                            'next month',
                                                    }}
                                                />
                                            </GridItem>
                                            <GridItem
                                                xs={6}
                                                className={
                                                    classes.firstColumnPadding
                                                }
                                            >
                                                <KeyboardDatePicker
                                                    disabled={
                                                        (formState.projectPhase
                                                            .value || '') === ''
                                                    }
                                                    size="small"
                                                    data-testid={
                                                        'dvFromPhaseEndDate'
                                                    }
                                                    disableToolbar
                                                    format={dateFormat}
                                                    autoOk
                                                    label="Planned Phase Completion"
                                                    helperText={validateEndDateAndSetMessage()}
                                                    error={
                                                        !(
                                                            validateDate(
                                                                formState
                                                                    .phaseEndDate
                                                                    .value,
                                                                dateFormat
                                                            ) &&
                                                            isDateRangeValid(
                                                                formState
                                                                    .phaseStartDate
                                                                    .value!,
                                                                formState
                                                                    .phaseEndDate
                                                                    .value!,
                                                                dateFormat
                                                            )
                                                        ) ||
                                                        hasError(
                                                            formState.phaseEndDate
                                                        )
                                                    }
                                                    name="phaseEndDate"
                                                    value={
                                                        formState.phaseEndDate
                                                            .value === ''
                                                            ? null
                                                            : formatDateWithTimezone(
                                                                  formState
                                                                      .phaseEndDate
                                                                      .value ||
                                                                      ''
                                                              )
                                                    }
                                                    minDate={
                                                        formState.phaseStartDate
                                                            .value === ''
                                                            ? null
                                                            : formState
                                                                  .phaseStartDate
                                                                  .value
                                                    }
                                                    required={
                                                        formState.phaseEndDate
                                                            .required
                                                    }
                                                    onChange={(
                                                        date: Date | null,
                                                        value:
                                                            | string
                                                            | null
                                                            | undefined
                                                    ) =>
                                                        handleDateChange(
                                                            date,
                                                            'phaseEndDate',
                                                            value
                                                        )
                                                    }
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    PopoverProps={{
                                                        style: { zIndex: 1302 },
                                                    }}
                                                    inputProps={{
                                                        'aria-label':
                                                            'planned phase completion',
                                                    }}
                                                    KeyboardButtonProps={{
                                                        edge: 'end',
                                                        'aria-label':
                                                            'date picker button',
                                                    }}
                                                    InputAdornmentProps={{
                                                        className:
                                                            classes.dateAdornment,
                                                    }}
                                                    TextFieldComponent={
                                                        CalendarTextField
                                                    }
                                                    leftArrowButtonProps={{
                                                        'aria-label':
                                                            'previous month',
                                                    }}
                                                    rightArrowButtonProps={{
                                                        'aria-label':
                                                            'next month',
                                                    }}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </MuiPickersUtilsProvider>
                                </>
                            )}
                        </div>
                    </GridItem>
                </GridContainer>
                <GridContainer
                    spacing={3}
                    className={clsx(
                        classes.marginUnset,
                        classes.row,
                        classes.paddingLeft,
                        classes.footer
                    )}
                    justifyContent="center"
                    alignItems="center"
                >
                    <GridItem
                        xs={6}
                        className={clsx(
                            classes.firstColumnPadding,
                            classes.saveButtonCell
                        )}
                    >
                        <RoundButton
                            size="large"
                            hasShadow={false}
                            disabled={
                                firmProjectGroupLoading ||
                                createProjectLoading ||
                                updateProjectLoading
                            }
                            loading={
                                firmProjectGroupLoading ||
                                createProjectLoading ||
                                updateProjectLoading
                            }
                            color="#FFF"
                            borderColor="#A58968"
                            backgroundColor={theme.palette.secondary.main}
                            data-testid="saveProject"
                            hoverColor="#E69C24"
                            onClick={onBtnSave}
                        >
                            <Typography className={classes.buttonText}>
                                {isEditMode ? 'SAVE' : 'CREATE'}
                            </Typography>
                        </RoundButton>
                    </GridItem>
                    <GridItem xs={6} className={classes.cancelButtonCell}>
                        <RoundButton
                            size="large"
                            disabled={
                                createProjectLoading || updateProjectLoading
                            }
                            hasShadow={false}
                            color={theme.palette.secondary.main}
                            borderColor={theme.palette.secondary.main}
                            hoverColor="#A58968"
                            backgroundColor="#fff"
                            data-testid="cancelNewProject"
                            onClick={onCancel}
                        >
                            <Typography className={classes.buttonText}>
                                CANCEL
                            </Typography>
                        </RoundButton>
                    </GridItem>
                </GridContainer>
            </div>
            {isLevelOfSpecificationConfirmationDialogOpen && (
                <ConfirmationDialog
                    openDialog={isLevelOfSpecificationConfirmationDialogOpen}
                    handleDialogClose={handleConfirmationDialogClose}
                    dialogActionCallback={handleConfirmationDialog}
                    dialogHeading="Confirm LOS Change"
                    confirmMessage="Do you want to proceed?"
                    contentNode={
                        <>
                            A Level of Specification (LOS) is associated to
                            non-graphic information across the MasterSpec
                            library. Modifying the LOS will result in content
                            being activated or inactivated project-wide.
                        </>
                    }
                    cancelText="Cancel"
                    confirmText="Yes"
                    size="small"
                    contentStyles={{
                        padding: 0,
                        borderTop: 'none',
                        borderBottom: 'none',
                        width: pxToRem(585),
                    }}
                    dialogStyles={{
                        zIndex: theme.zIndex.modal + 2,
                    }}
                />
            )}
            {createProjectInProgress && (
                <ProgressDialog
                    dialogId={'newProjectProgressBar'}
                    dialogName={'Applying Best Practices'}
                    open={true}
                    statusText={
                        'Specpoint is applying your Best Practices to the Project. You will be notified when the process is complete'
                    }
                    progressPercentage={progress}
                    variant="New Project"
                    showProgressPercentage
                />
            )}

            {/* Confirmation Dialog for Remove Owner */}
            {RemoveOwnerConfirmationDialog}

            <Backdrop
                className={classes.backdrop}
                open={firmProjectGroupLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}
export default NewProject
